/* charger — WIND-UP then RUSH, eight frames.
 *
 * The charger's whole job is to be seen coming. It picks a line, tells you,
 * and runs it. Phase 0 is `S.charger_0` verbatim and phase 4 is `S.charger_1`
 * with only its pared outline column drawn back on (see below); everything
 * between them is drawn from the same torso, so the animal is the same animal
 * at every phase.
 *
 *   0    SET.     Both feet flat and wide, back level, horns neutral.
 *   1-2  WIND-UP. The shoulders bunch into a hump while the core SINKS under
 *        it, then the hump lets go and the whole body drops again into the
 *        deepest crouch, both horns drawn back and the snout pulled in. The
 *        legs it folds away come back as width: the belly spreads to both cell
 *        edges and both hooves plant flat and long. Cocking.
 *   3-4  RUSH.    It unloads. Horns snap back to neutral, the rear leg swings
 *        out behind and drives a row BELOW the ground line on its toe while
 *        the front folds up — folded, not deleted: the lifted hoof is still
 *        drawn, tucked under the belly. Phase 4 is the shipped drive pose:
 *        body squashed narrow, both feet on their toes.
 *   5-7  ...and through. 5 is the LAUNCH: the body rides a row higher than
 *        standing, the legs are splayed to their widest and both horns are
 *        fully forward, but the rear toe has not left row 13 yet — it is the
 *        last of the push. 6 is the strike: the front hoof slams flat on row
 *        14 and takes the whole animal while the rear leg swings through under
 *        the belly. 7 catches — the legs pass under the chest, the rear hoof
 *        landing flat while the front is still up on its toe, the horns settle
 *        to neutral, and one frame on the body rises a row and the stance
 *        opens back out: phase 0.
 *
 * Four rules held the drawing together.
 *
 * THE HORNS ARE THE TELL, SO THEY MOVE THE OUTLINE. Sliding the pale `u` horn
 * pixel around inside a fixed `oo` cap is a recolour, not a tell — the
 * silhouette never changes and at 16px nobody reads it. So back / neutral /
 * forward take the horn rows' right edge to x13 / x14 / x15, and the snout
 * row follows: drawn in for the two cocked frames, out for the rest. Measured
 * as the right edge of the horn row, the schedule is 14, 13, 13, 14, 15, 15,
 * 15, 14 — the reversal lands a frame before the body commits. Phase 4 reads
 * 15 rather than the 14 the pose wants because the shipped squash flares its
 * outline at both ends; it sits between two genuine 15s, so it does not break
 * the ramp.
 *
 * THE BODY MOVES EVERY FRAME. The torso is a striped slab, so translating it
 * one row costs ~70 changed pixels while a leg or horn edit costs ~20. Park
 * it and the cycle becomes two heights with tweens between them no matter how
 * much the feet flail. The slab's top row runs 4, 4(+hump), 6, 5, 5, 4, 5, 5:
 * a real arc with a trough at the coil and a peak at the launch, and only two
 * transitions (3→4 and 6→7) that hold height, which are the two passing beats
 * where the legs do the work instead.
 *
 * ROW 13 IS THE FLOOR, AND SOMETHING IS ALWAYS ON IT. Row 14 is spent only
 * where a foot is genuinely pushing or landing (3, 4, 6), so the lowest lit
 * row runs 13/13/13/14/14/13/14/13 — one row of travel, all of it a toe
 * digging in, never the animal hopping. This is the one place the earlier
 * draft cheated: it emptied row 13 on the launch frame to say "airborne", and
 * a floor that drops two rows in an eight-frame loop does not read as air, it
 * reads as the whole creature bouncing. Air is drawn instead by the FRONT foot
 * leaving — thrown out ahead of the shoulder with its hoof clear of the
 * ground — while the rear toe is still down, pushing. Feet stay down; the body
 * rises.
 *
 * MASS IS TRADED, NEVER SPENT. Ink runs 117/114/110/112/110/113/114/111 — a
 * 6.2% swing about the median, inside the 10% the check allows and in the same
 * band as sporeling. Nothing here was padded to hit that number; every frame
 * that was light was light because a limb had been erased rather than moved,
 * so each one pays for the pixels it lifts:
 *
 *   2 (coil)   folds two rows of leg away, and gets them back as a belly
 *              squashed out to both cell edges plus two flat, weight-bearing
 *              hooves. A squat loses leg and gains body; it does not shrink.
 *   3 (unload) lifts the front foot, so the front hoof is redrawn tucked at
 *              the belly and the rear toe gains the row it drove below.
 *   4 (drive)  gets back the outline column `bobbed()` pared off each end. The
 *              compression is carried by the narrow ribcage stripe, which is
 *              what the pose was always about; the missing edge was a hole,
 *              not a squash, and it cost the frame seven pixels.
 *   5 (launch) trades the front leg's ground contact for the rear leg's extra
 *              length — it has to reach further now that the body is up.
 *   7 (catch)  passes the legs under the chest, so the rear hoof widens as it
 *              takes the weight by exactly what the front loses going up on
 *              its toe.
 *
 * Measured: steps 73/81/83/24/97/100/33/90 px, spread 1.20x median, mass 6.2%,
 * floor drift 1 row, and no two of the eight frames — adjacent or not — are
 * closer than 24 px.
 */

// phase 0 — SET. `S.charger_0` verbatim. Both feet flat and wide, weight
// even, back level, horns neutral and the snout tip out at the cell edge.
export const charger_0 = [
  '................',
  '................',
  '................',
  '................',
  '.....ooooooo....',
  '...ooRRRRRRRuoo.',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRerR',
  'ooRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRuo.',
  'ooRRRoooooRRRo..',
  '.oRRo.....oRRo..',
  '.oRRo.....oRRo..',
  '.oooo.....oooo..',
  '................',
  '................',
];

// phase 1 — LOAD. The shoulders bunch into a hump and the core sinks a row
// under it, so the sprite gets TALLER at the back and LOWER through the
// middle at the same time — the shape of an animal gathering. The upper horn
// draws back a pixel; the stance narrows and both feet stay flat.
export const charger_1 = [
  '................',
  '................',
  '................',
  '................',
  '.......ooooo....',
  '.....ooRRRRRo...',
  '...ooRRRRRRuoo..',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRerR',
  'ooRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRuo.',
  'oooRRRooooRRRo..',
  '..oRRo....oRRo..',
  '..oooo....oooo..',
  '................',
  '................',
];

// phase 2 — COIL. Deepest crouch: the hump lets go and the whole body drops a
// second row while the feet hold row 13. The two rows of leg it folds away are
// paid back in width — the belly spreads to both cell edges and the haunches
// bunch — and in footing: both hooves flat and six wide, because at the bottom
// of a crouch the feet are carrying everything. Nothing is deleted, only moved
// down and out, which is why the frame is not eleven pixels lighter than its
// neighbours any more. BOTH horns are back and the snout is drawn in — the head
// is cocked, and this is the frame the player is meant to read.
export const charger_2 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '................',
  '...ooooooooo....',
  '..oooRRRRRRuoo..',
  'ooRRRRrrrrrRRRRo',
  'ooRRRRRRRRRRRer.',
  'ooRRRRrrrrrRRRRo',
  'ooRRRRRRRRRRRuo.',
  'ooRRRRoooRRRRoo.',
  '.oooooo..oooooo.',
  '................',
  '................',
];

// phase 3 — UNLOAD. The body springs back up a row and the head stops moving
// away from you: horns neutral, snout tip out again. The rear leg swings out
// behind the hip and drives a row BELOW the ground line onto its toe, and the
// front leg folds up off the floor — folded, not erased: the raised hoof is
// still drawn, tucked in under the belly on row 13, and the rear toe gains the
// row it drove below. One foot on the ground, both pointing the same way. It is
// committed here.
export const charger_3 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....ooooooo....',
  '...ooRRRRRRRuoo.',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRerR',
  'ooRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRuo.',
  'ooRRRoooooRRRoo.',
  'oRRo......oRRo..',
  'oRRo.......ooo..',
  'ooo.............',
  '................',
];

// phase 4 — DRIVE. `S.charger_1`: the body squashed narrow through the
// ribcage, both legs long and both feet up on their toes below the ground line.
// Everything is pushing the same way at once. (The shipped squash was baked by
// `bobbed()`, which pares a pixel off each end where a sprite is already drawn
// to the cell edge — so this frame arrived with no outline column on its widest
// rows, seven pixels light. The column is drawn back on here. The pose is
// unchanged and still the one the character is recognised by; the compression
// was never in the missing edge, it is in the narrow ribcage stripe, and a
// missing edge in one frame of eight reads as a hole in the animal.)
export const charger_4 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '......ooooo.....',
  '..oooRRRRRRRuooo',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRer.',
  '.oRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRuo.',
  '.oRRRoooooRRRo..',
  '.oRRo.....oRRo..',
  '.oRRo.....oRRo..',
  '..o.o......o.o..',
  '................',
];

// phase 5 — LAUNCH. The body rides a row HIGHER than standing and the legs are
// splayed to their widest, rear trailing behind the hip and front thrown out
// ahead of the shoulder, both horns a pixel FORWARD of neutral with the snout
// at full reach. What leaves the ground is the FRONT hoof — clear of the floor,
// its pixels spent lengthening the rear leg, which now has further to reach and
// is still on its toe on row 13, finishing the push. Emptying row 13 outright
// would drop the floor two rows across the loop, and a floor that moves does
// not read as air; it reads as the whole animal bouncing.
export const charger_5 = [
  '................',
  '................',
  '................',
  '................',
  '.....ooooooo....',
  '...ooRRRRRRRRuoo',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRerR',
  'ooRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRRuo',
  'oRRRoooooooRRRo.',
  'oRRo.......oRRo.',
  'oRRo........oo..',
  'ooo.............',
  '................',
  '................',
];

// phase 6 — STRIKE. The body drops back onto the front hoof, which slams flat
// a row below the ground line and takes the whole animal, while the rear leg
// swings through under the belly with its foot tucked. Horns still fully out
// front — the head has not recovered yet, which is why this frame is the one
// that hurts.
export const charger_6 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....ooooooo....',
  '...ooRRRRRRRRuoo',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRerR',
  'ooRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRRuo',
  'ooooRRRooooRRRo.',
  '....oRRo...oRRo.',
  '.....oo....oRRo.',
  '...........oooo.',
  '................',
];

// phase 7 — CATCH. The horns settle back to neutral and the legs pass under
// the chest — rear coming forward and landing FLAT, its hoof widening by
// exactly what the front loses as it rises onto its toe and swings back. It is
// the mirror of the coil's gathered stance, but a row higher and travelling the
// other way. One frame on the body rises to standing and the legs open out to
// their full width again: phase 0, set.
export const charger_7 = [
  '................',
  '................',
  '................',
  '................',
  '................',
  '.....ooooooo....',
  '...ooRRRRRRRuoo.',
  '..oRRRrrrrrRRRRo',
  '.oRRRRRRRRRRRerR',
  'ooRRRRrrrrrRRRRo',
  'oRRRRRRRRRRRRuo.',
  'oooRRRoooRRRRoo.',
  '..oRRRo..oRRo...',
  '..ooooo...ooo...',
  '................',
  '................',
];
